// ignore-string-externalization

import {
  Type,
  cssColorValue,
  spacer8,
  spacer12,
  spacer16,
  spacer20,
  spacer24,
  screenSmMax,
  screenXsMax,
  screenXxsMax,
  screenXsMin,
  screenSmMin,
  mobileCello,
  mobileCanon,
} from '@spotify-internal/encore-web-v6';
import {
  DELAYS,
  fadeInAnimation,
  withAnimationContext,
} from '@spotify-internal/frodor-modules';
import styled, { css } from 'styled-components';
import { hexToRgba } from '../../../features/src/color-conversion';
import { ImageAspectBox16x9, ButtonTertiaryAsLink, UnstyledLink } from '../ui';
import { transformScaleUp } from '../utils';

export const CTALink = styled(ButtonTertiaryAsLink)`
  padding-left: 0;
  text-align: left;
  color: ${props => props.theme.link?.color};
  svg {
    margin-left: ${spacer8};
    vertical-align: -2px;
  }
  &&:hover:not(:disabled),
  &:focus:not(:disabled),
  &:active:not(:disabled) {
    color: ${props => hexToRgba(props.theme.link?.color, 0.5)};
    outline: none;
  }
`;

type ImageWrapperType = { video: boolean; featured: boolean };

export const ImageWrapper = styled(ImageAspectBox16x9)<ImageWrapperType>`
  overflow: hidden;

  /* for videos add a black gradient overlay at the bottom to make the play button
     slightly more visible */
  ${props =>
    props.video &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 0;
        height: 100%;
        width: 100%;
        box-shadow: inset 0 -90px 10px 0 rgba(0, 0, 0, 0.1);
        z-index: 2;
      }
    `}

  /* play icon */
  svg {
    display: ${props => (props.video ? 'initial' : 'none')};
    position: absolute;
    bottom: ${props => (props.featured ? spacer24 : spacer16)};
    left: ${props => (props.featured ? spacer24 : spacer16)};
    width: ${props => (props.featured ? '48px' : '32px')};
    height: auto;
    color: ${cssColorValue('backgroundBase')};
    z-index: 3;

    @media (max-width: ${screenXsMax}) {
      bottom: ${spacer12};
      left: ${spacer12};
    }

    @media (max-width: ${screenSmMax}) {
      bottom: ${spacer16};
      left: ${spacer16};
    }
  }

  &:hover {
    img,
    svg {
      ${transformScaleUp};
      transition: transform 0.3s ease-in-out;
    }
    svg {
      opacity: 0.8;
    }
  }
`;

export const ItemLabel = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'finale',
    weight: 'book',
  })`
    ${fadeInAnimation(DELAYS.FOURTH)};
    padding-top: ${spacer20};
    padding-bottom: 0;
    text-transform: uppercase;
    color: ${props => props.theme.label.color};
  `,
);

export const ItemTitle = withAnimationContext(
  styled(Type.h3).attrs({
    variant: 'celloCanon',
    weight: 'bold',
  })`
    padding-top: ${spacer16};
    margin-bottom: ${spacer8};

    ${fadeInAnimation(DELAYS.FIFTH)};

    @media (min-width: ${screenSmMin}) {
      max-height: 3.6em;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    @media (max-width: ${screenSmMax}) {
      padding-top: ${spacer8};
      margin-bottom: 0;
    }

    @media (min-width: ${screenXsMin}) and (max-width: ${screenSmMax}) {
      margin-top: ${spacer16};
      margin-bottom: ${spacer8};
      ${() => mobileCanon}
    }

    @media (max-width: ${screenXxsMax}) {
      ${() => mobileCello}
    }
  `,
);

export const ItemText = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'ballad',
  })`
    ${fadeInAnimation(DELAYS.SIXTH)};
    /* no description text on mobile */
    @media (max-width: ${screenXxsMax}) {
      display: none;
    }
  `,
);

export const StyledLink = styled(UnstyledLink)<any>`
  text-decoration: none;
  color: ${cssColorValue('textBase')};

  &:hover,
  &:focus:not(:disabled),
  &:active:not(:disabled) {
    text-decoration: none;
    color: ${cssColorValue('textSubdued')};
  }

  &:hover {
    img,
    svg {
      ${transformScaleUp};
      transition: transform 0.3s ease-in-out;
    }
    svg {
      opacity: 0.8;
    }
  }
`;

export const LoadMoreButtonWrapper = styled.div`
  text-align: center;
  width: 100%;
  margin-bottom: ${spacer20};
`;
